import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';            

/**
 * Get inventory dashboard statistics
 * @returns {Promise<Object>} Dashboard statistics
 */
export const getDashboardStatistics = async () => {
  const response = await axiosInstance.get('/Inventory/dashboard');
  return response.data;
};

/**
 * Get paginated inventory items with filtering and search
 * @param {Object} params - Query parameters
 * @param {string} params.searchTerm - Search term
 * @param {string} params.filter - Filter type: 'all', 'lowstock', 'outofstock'
 * @param {number} params.page - Page number (1-based)
 * @param {number} params.pageSize - Items per page
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort order: 'asc' or 'desc'
 * @returns {Promise<Object>} Paginated inventory response
 */
export const getInventoryItems = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
  if (params.filter) queryParams.append('filter', params.filter);
  if (params.page) queryParams.append('page', params.page);
  if (params.pageSize) queryParams.append('pageSize', params.pageSize);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  
  const response = await axiosInstance.get(`/Inventory/items?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get all inventory items (legacy - for backward compatibility)
 * @returns {Promise<Array>} List of inventory items
 */
export const getAllInventory = async () => {
  const response = await axiosInstance.get('/Inventory');
  return response.data;
};

/**
 * Get inventory by product ID (legacy)
 * @param {number} productId - Product ID
 * @returns {Promise<Object>} Inventory details
 */
export const getInventoryByProduct = async (productId) => {
  const response = await axiosInstance.get(`/Inventory/product/${productId}`);
  return response.data;
};

/**
 * Get detailed inventory item information by product ID
 * @param {number} productId - Product ID
 * @returns {Promise<Object>} Detailed inventory item
 */
export const getInventoryItemDetails = async (productId) => {
  const response = await axiosInstance.get(`/Inventory/items/${productId}`);
  return response.data;
};

/**
 * Get low stock items
 * @returns {Promise<Array>} List of low stock items
 */
export const getLowStockItems = async () => {
  const response = await axiosInstance.get('/Inventory/low-stock');
  return response.data;
};

/**
 * Get out of stock items
 * @returns {Promise<Array>} List of out of stock items
 */
export const getOutOfStockItems = async () => {
  const response = await axiosInstance.get('/Inventory/out-of-stock');
  return response.data;
};

/**
 * Adjust inventory manually
 * @param {Object} adjustmentData - Adjustment data
 * @param {number} adjustmentData.ProductId - Product ID
 * @param {number} adjustmentData.QuantityChange - Quantity change (positive or negative)
 * @param {string} adjustmentData.Reason - Reason for adjustment
 * @param {string} [adjustmentData.ReferenceNumber] - Optional reference number
 * @returns {Promise<Object>} Updated inventory
 */
export const adjustInventory = async (adjustmentData) => {
  const response = await axiosInstance.post('/Inventory/adjust', adjustmentData);
  return response.data;
};

/**
 * Bulk adjust inventory
 * @param {Array} adjustments - Array of adjustment data
 * @returns {Promise<Array>} Updated inventory items
 */
export const bulkAdjustInventory = async (adjustments) => {
  const response = await axiosInstance.post('/Inventory/bulk-adjust', adjustments);
  return response.data;
};

/**
 * Get inventory transaction history for a product
 * @param {number} productId - Product ID
 * @param {number} [page=1] - Page number
 * @param {number} [pageSize=20] - Items per page
 * @returns {Promise<Object>} Paginated transaction history
 */
export const getProductTransactionHistory = async (productId, page = 1, pageSize = 20) => {
  const response = await axiosInstance.get(`/Inventory/transactions/product/${productId}?page=${page}&pageSize=${pageSize}`);
  return response.data;
};

/**
 * Get all inventory alerts
 * @returns {Promise<Array>} List of alerts
 */
export const getInventoryAlerts = async () => {
  const response = await axiosInstance.get('/Inventory/alerts');
  return response.data;
};

/**
 * Acknowledge an alert
 * @param {number} alertId - Alert ID
 * @returns {Promise<Object>} Acknowledged alert
 */
export const acknowledgeAlert = async (alertId) => {
  const response = await axiosInstance.post(`/Inventory/alerts/${alertId}/acknowledge`);
  return response.data;
};

/**
 * Reverse a transaction
 * @param {number} transactionId - Transaction ID
 * @returns {Promise<Object>} Reversed transaction
 */
export const reverseTransaction = async (transactionId) => {
  const response = await axiosInstance.post(`/Inventory/reverse/${transactionId}`);
  return response.data;
};

/**
 * Get inventory statistics (legacy - kept for backward compatibility)
 * @returns {Promise<Object>} Inventory statistics
 */
export const getInventoryStatistics = async () => {
  const response = await axiosInstance.get('/Inventory/statistics');
  return response.data;
};

/**
 * Reduce stock after ticket creation (automatic)
 * @param {number} ticketId - Ticket ID
 * @returns {Promise<Object>} Stock reduction result
 */
export const reduceStockForTicket = async (ticketId) => {
  const response = await axiosInstance.post(`/Inventory/reduce-for-ticket/${ticketId}`);
  return response.data;
};

/**
 * Check if products are in stock
 * @param {Array} items - Array of {productId, quantity}
 * @returns {Promise<Object>} Stock availability result
 */
export const checkStockAvailability = async (items) => {
  const response = await axiosInstance.post('/Inventory/check-availability', items);
  return response.data;
};

/**
 * Get paginated low stock report
 * @param {number} [page=1] - Page number
 * @param {number} [pageSize=20] - Items per page
 * @returns {Promise<Object>} Paginated low stock report
 */
export const getLowStockReport = async (page = 1, pageSize = 20) => {
  const response = await axiosInstance.get(`/Inventory/reports/low-stock?page=${page}&pageSize=${pageSize}`);
  return response.data;
};

/**
 * Get paginated overstock report
 * @param {number} [page=1] - Page number
 * @param {number} [pageSize=20] - Items per page
 * @returns {Promise<Object>} Paginated overstock report
 */
export const getOverstockReport = async (page = 1, pageSize = 20) => {
  const response = await axiosInstance.get(`/Inventory/reports/overstock?page=${page}&pageSize=${pageSize}`);
  return response.data;
};

/**
 * Get paginated best selling products
 * @param {string} [startDate] - Start date (ISO string)
 * @param {string} [endDate] - End date (ISO string)
 * @param {number} [page=1] - Page number
 * @param {number} [pageSize=20] - Items per page
 * @returns {Promise<Object>} Paginated best sellers report
 */
export const getBestSellers = async (startDate = null, endDate = null, page = 1, pageSize = 20) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  params.append('page', page);
  params.append('pageSize', pageSize);
  
  const response = await axiosInstance.get(`/Inventory/reports/best-sellers?${params.toString()}`);
  return response.data;
};

/**
 * Get paginated out of stock report
 * @param {number} [page=1] - Page number
 * @param {number} [pageSize=20] - Items per page
 * @returns {Promise<Object>} Paginated out of stock report
 */
export const getOutOfStockReport = async (page = 1, pageSize = 20) => {
  const response = await axiosInstance.get(`/Inventory/reports/out-of-stock?page=${page}&pageSize=${pageSize}`);
  return response.data;
};

/**
 * Mark a product as obsolete
 * @param {number} productId - Product ID
 * @returns {Promise<Object>} Updated product
 */
export const markProductAsObsolete = async (productId) => {
  const response = await axiosInstance.post(`/Inventory/products/${productId}/obsolete`);
  return response.data;
};

/**
 * Mark an obsolete product as active
 * @param {number} productId - Product ID
 * @returns {Promise<Object>} Updated product
 */
export const markProductAsActive = async (productId) => {
  const response = await axiosInstance.post(`/Inventory/products/${productId}/activate`);
  return response.data;
};

// Export all functions
export default {
  getDashboardStatistics,
  getInventoryItems,
  getAllInventory,
  getInventoryByProduct,
  getInventoryItemDetails,
  getLowStockItems,
  getOutOfStockItems,
  adjustInventory,
  bulkAdjustInventory,
  getProductTransactionHistory,
  getInventoryAlerts,
  acknowledgeAlert,
  reverseTransaction,
  getInventoryStatistics,
  reduceStockForTicket,
  checkStockAvailability,
  getLowStockReport,
  getOverstockReport,
  getOutOfStockReport,
  getBestSellers,
  markProductAsObsolete,
  markProductAsActive,
};